import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import {
  addBedNumberApi,
  updateBedNumberApi,
} from "../../../services/bed-number.service";
import { getBedSubCategoryApi } from "../../../services/bed-sub-category.service";
import { useGetApi } from "../../../hooks/useGetApi";
import BedNumberSchema from "../../../joi/master/bed-number.schema";

export default function BedNumberTwoModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    bedSubCategory: null,
    bedNumber: "",
    cost: 0,
    active: true,
    changeRateOnTransfer: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const { dataList: bedSubCategoryList } = useGetApi({
    apiFunction: getBedSubCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "bedSubCategories",
    countKey: "total",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevValue) => {
      return {
        ...prevValue,
        [name]:
          type === "checkbox"
            ? checked
            : name === "cost"
            ? Number(value)
            : value,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    const { error } = BedNumberSchema.validate({
      ...formData,
      bedSubCategory: formData?.bedSubCategory?._id,
    });

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }

    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateBedNumberApi({
        ...formData,
        bedSubCategory: formData?.bedSubCategory?._id,
      });
    } else {
      response = await addBedNumberApi({
        ...formData,
        bedSubCategory: formData?.bedSubCategory?._id,
      });
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `Bed Number ${selectedRow?._id ? "updated" : "added"} successfully`
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  // Sync selectedRow data when modal opens
  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        bedSubCategory: selectedRow?.bedSubCategory || null,
        bedNumber: selectedRow?.bedNumber || "",
        cost: selectedRow?.cost ?? 0,
        active: selectedRow?.active ?? true,
        changeRateOnTransfer: selectedRow?.changeRateOnTransfer ?? true,
      });
    } else {
      setFormData(initialState);
    }
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            {selectedRow?._id ? `Edit Bed` : `Add Bed`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={bedSubCategoryList || []}
                getOptionLabel={(option) => option?.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bed Sub Category"
                    name="bedSubCategory"
                    required
                    fullWidth
                  />
                )}
                value={formData?.bedSubCategory || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "bedSubCategory", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Bed Number"
                name="bedNumber"
                required
                fullWidth
                value={formData?.bedNumber || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Cost"
                name="cost"
                type="number"
                fullWidth
                value={formData?.cost || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData?.active}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="changeRateOnTransfer"
                    checked={formData?.changeRateOnTransfer}
                    onChange={handleChange}
                  />
                }
                label="Change Rate On Transfer?"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Update"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
